import { Box, keyframes } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

// A highlight band swept across the block. The gradient is 4× the element's
// width, so moving the background position slides the band through it.
const shimmerSweep = keyframes`
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
`;

export interface ShimmerProps {
  /** Any CSS width — px, %, or a responsive breakpoint object. */
  width?: number | string | Record<string, number | string>;
  height?: number | string | Record<string, number | string>;
  /** Corner radius; pass '50%' for avatars. */
  radius?: number | string;
  sx?: SxProps<Theme>;
}

/**
 * The single loading primitive every skeleton is built from: a neutral block
 * with a shimmer sweeping through it.
 *
 * Honours `prefers-reduced-motion` — the sweep is replaced by a flat block, so
 * the layout still reads as loading without the movement.
 */
export const Shimmer = ({ width = '100%', height = 16, radius = '6px', sx }: ShimmerProps) => (
  <Box
    aria-hidden
    sx={[
      (theme) => ({
        width,
        height,
        borderRadius: radius,
        flexShrink: 0,
        backgroundColor: theme.palette.grey[100],
        backgroundImage: `linear-gradient(
          90deg,
          ${theme.palette.grey[100]} 0%,
          ${theme.palette.grey[200]} 20%,
          ${theme.palette.grey[100]} 40%,
          ${theme.palette.grey[100]} 100%
        )`,
        backgroundSize: '400% 100%',
        animation: `${shimmerSweep} 1.6s ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          backgroundImage: 'none',
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  />
);
